import { useQuery } from "@hooks/useQuery";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DynamicForm, FormConfig } from "~/Components/DynamicForm/DynamicForm";
import { useNotification } from "~/Hooks/useNotification";
import { post, put } from "~/Services";
import { magpHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { useDocumentDetails } from "../../../ApprovalManagement/Drilldown.Hooks";
import { useManualWelcomePage } from "../Manual.hook";
import { fields } from "../manualConstants";
import { Loader } from "~/Components";

export const ManualWelcomePage: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const query = useQuery();
  const { openToast } = useNotification();
  const approveQuery = new URLSearchParams(location.search);
  const approveId = approveQuery.get("id");

  const {
    data: manualWelcomePageData,
    error,
    isLoading,
    isPending,
    isError,
    refetch: refetchWelcomePage,
  } = useManualWelcomePage();
  const {
    data: manualWelcomePageDataById,
    isLoading: isLoadingManualWelcomePage,
    isPending: isPendingManualDataById,
    refetch: refetchData,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (!query.get("id")) {
      refetchWelcomePage();
    }
  }, []);
  useEffect(() => {
    if (approveId) {
      refetchData();
    }
  }, [approveId]);

  const ManualWelcomePageFormConfig: FormConfig = {
    initialValues: {
      field_introduction_manual_header:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_introduction_manual_header)
          : decodeHTML(manualWelcomePageData?.[0]?.field_introduction_manual_header),

      field_malay_introduction_manual:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_malay_introduction_manual)
          : decodeHTML(manualWelcomePageData?.[0]?.field_malay_introduction_manual),

      field_mandarin_introduction_manu:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_mandarin_introduction_manu)
          : decodeHTML(manualWelcomePageData?.[0]?.field_mandarin_introduction_manu),

      field_manual_welcome_sub_text:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_manual_welcome_sub_text)
          : decodeHTML(manualWelcomePageData?.[0]?.field_manual_welcome_sub_text),

      field_malay_manual_welcome_sub_t:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_malay_manual_welcome_sub_t)
          : decodeHTML(manualWelcomePageData?.[0]?.field_malay_manual_welcome_sub_t),

      field_mandarin_manu_welc_sub_tex:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_mandarin_manu_welc_sub_tex)
          : decodeHTML(manualWelcomePageData?.[0]?.field_mandarin_manu_welc_sub_tex),

      field_manual_welcome_description:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_manual_welcome_description)
          : decodeHTML(manualWelcomePageData?.[0]?.field_manual_welcome_description),

      field_malay_manual_welcome_descr:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_malay_manual_welcome_descr)
          : decodeHTML(manualWelcomePageData?.[0]?.field_malay_manual_welcome_descr),

      field_mandarin_manual_welcom_des:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_mandarin_manual_welcom_des)
          : decodeHTML(manualWelcomePageData?.[0]?.field_mandarin_manual_welcom_des),

      field_manual_welcom_continue:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_manual_welcom_continue)
          : decodeHTML(manualWelcomePageData?.[0]?.field_manual_welcom_continue),

      field_malay_manual_continue_text:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_malay_manual_continue_text)
          : decodeHTML(manualWelcomePageData?.[0]?.field_malay_manual_continue_text),

      field_mandarin_manual_continue:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML(manualWelcomePageDataById?.payload?.field_mandarin_manual_continue)
          : decodeHTML(manualWelcomePageData?.[0]?.field_mandarin_manual_continue),

      field_magp_image:
        manualWelcomePageDataById?.payload && !isLoadingManualWelcomePage
          ? decodeHTML([
              {
                uid: "-1",
                name: "field_content_english_image.png",
                status: "done",
                url: manualWelcomePageDataById?.payload?.field_magp_image,
              },
            ])
          : decodeHTML([
              {
                uid: "-1",
                name: "field_content_english_image.png",
                status: "done",
                url: manualWelcomePageData?.[0]?.field_magp_image,
              },
            ]),
    },
    fields: fields.manualWelcomePage,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let excludedKeys: any = [];
    let imageKeys: any = ["field_magp_image"];

    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field && field[0]) {
          if (field[0].originFileObj) {
            const formData = new FormData();
            formData.append("upload-file", field[0].originFileObj);
            formData.append("application", "MAGP");
            const res: any = await post("management/upload-file", formData);

            if (res) {
              data[key] = res.response.data;
            }
          } else {
            data[key] = data[key][0].url;
          }
        }
      } catch (error) {}
    }
    const payload = {
      action: action,
      method: method,
      module: "Manual",
      subModule: query?.get("activeTab"),
      type: "DEDICATED",
      application: "MAGP",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAGP",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...data,
        published: true,
        createdAt: "2024-07-16T07:44:25",
        updatedAt: "2024-07-16T07:44:30",
      },
      status: status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response:any) => {
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          refetchData();
        })
        .catch((err: any) => {
          console.log("manual-error", err);
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response:any) => {
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          refetchData();
        })
        .catch((err: any) => {
          console.log("manual-error-publish", err);
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  if (isLoading || isLoadingManualWelcomePage) {
    return <Loader />;
  }

  return (
    <>
      <DynamicForm
        isLoading={isLoading || isLoadingManualWelcomePage}
        config={ManualWelcomePageFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${magpHost}/api/manual-welcome/${manualWelcomePageData?.[0]?.id}`,
            "UPDATE",
            "PATCH",
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    </>
  );
};
